import React, { useMemo } from "react";
import { Button } from "antd";
import { useTranslation } from "react-i18next";
import ScheduledActionList from "../../../components/scheduledActions/ScheduledActionList";
import TimedActionEditModal from "../../../components/scheduledActions/TimedActionEditModal";
import { useTimedActionsCrud } from "../../../components/scheduledActions/useTimedActionsCrud";
import { useScheduledActionPermissions } from "../../../components/scheduledActions/useScheduledActionPermissions";
import {
  useControlOptions,
  TIMEZONE_DEFAULT,
} from "../../../components/scheduledActions/scheduledActionDefaults";
import { useBankFloorOpenings } from "../../../components/scheduledActions/useBankFloorOpenings";

interface ControlPanelBankScheduledActionsProps {
  bankId: string;
  organizationId: string;
  bankName?: string;
}

function ControlPanelBankScheduledActions({
  bankId,
  organizationId,
  bankName = undefined,
}: ControlPanelBankScheduledActionsProps): React.ReactElement | null {
  const [t] = useTranslation("global");
  const { canView, canCreate, canUpdate, canDelete } = useScheduledActionPermissions();

  const {
    actions,
    isFetching,
    fetchError,
    submitting,
    modalOpen,
    editDraft,
    closeModal,
    handleCreate,
    handleEdit,
    handleSubmit,
    handleDelete,
    handleToggleEnabled,
    alerts,
  } = useTimedActionsCrud({ kind: "bank", organizationId, bankId }, { enabled: canView });

  const controlOptions = useControlOptions();
  const bankNames: Record<string, string> = useMemo(
    () => (bankName !== undefined ? { [bankId]: bankName } : {}),
    [bankId, bankName],
  );

  /**
   * Target pickers for the editor: this bank, its cars, and its floors with
   * their openings.
   *
   * The bank list is the single bank this panel is scoped to. That is not a
   * limitation being worked around — a bank control panel is by definition about
   * one bank, and offering the org's other banks here would let an operator
   * schedule from a panel that then shows none of what they scheduled.
   */
  const bankIds = useMemo(() => [bankId], [bankId]);
  const {
    floorsByBank,
    liftsByBank,
    liftDoorsByBank,
    isLoading: floorsLoading,
    failures: floorLoadFailures,
  } = useBankFloorOpenings(bankIds);
  const selectors = useMemo(
    () => ({
      bankOptions: [{ id: bankId, name: bankName ?? bankId }],
      liftsByBank,
      floorsByBank,
      liftDoorsByBank,
      floorsLoading,
      floorLoadFailures,
    }),
    [
      bankId,
      bankName,
      liftsByBank,
      floorsByBank,
      liftDoorsByBank,
      floorsLoading,
      floorLoadFailures,
    ],
  );

  if (!canView) {
    return null;
  }

  return (
    <div>
      {canCreate && (
        <div style={{ marginBottom: 12 }}>
          <Button type="primary" onClick={() => handleCreate()}>
            {t("scheduledActions.addAction", "Add Scheduled Action")}
          </Button>
        </div>
      )}
      <ScheduledActionList
        actions={actions}
        loading={isFetching}
        error={fetchError}
        onEdit={canUpdate ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        onToggleEnabled={canUpdate ? handleToggleEnabled : undefined}
        bankNames={bankNames}
        controlOptions={controlOptions}
      />
      <TimedActionEditModal
        editDraft={editDraft}
        open={modalOpen}
        controlOptions={controlOptions}
        timezoneDefault={TIMEZONE_DEFAULT}
        submitting={submitting}
        onSubmit={handleSubmit}
        onCancel={closeModal}
        selectors={selectors}
      />
      {alerts}
    </div>
  );
}

export default ControlPanelBankScheduledActions;
